import React, { useState, useEffect } from 'react';
import {
  Button,
  Table,
  Modal,
  Input,
  Card,
  Tabs,
  DatePicker,
  Select,
  Space,
  message,
  Tooltip,
} from 'antd';
import {
  CloseOutlined,
  PauseOutlined,
  CheckOutlined,
  PlusOutlined,
  SettingOutlined,
  ProfileOutlined,
  FileTextOutlined,
  FieldTimeOutlined
} from '@ant-design/icons';
import moment from 'moment';
import axios from 'axios';
import AddLeave from './AddLeave';
import ManageLeave from './ManageLeave';
import { UserService } from '../../Services/Manage/UserService';

import LeaveService from "../../Services/Manage/LeaveService";
import ComplaintRaiseList from "./ComplaintRaiseList";


import SalarySlip from './SalarySlip';
import BreakType from './BreakType';
import { useNavigate } from "react-router-dom";

const getPermission = (link) => {
  try {
    const raw = localStorage.getItem("ome_permission");
    const parsed = JSON.parse(raw);
    const permissions = Array.isArray(parsed) ? parsed : JSON.parse(parsed);
    return permissions.find(p => p.link === link) || { add: false, edit: false, delete: false, read: false };
  } catch { return { add: false, edit: false, delete: false, read: false }; }
};


const { TextArea } = Input;
const { RangePicker } = DatePicker;
const { Option } = Select;

const DisplayLeave = () => {

  const perm = getPermission('/workforce-management');
  const [activeSection, setActiveSection] = useState('leave');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [comment, setComment] = useState('');
  const [searchText, setSearchText] = useState('');
  const [dateRange, setDateRange] = useState([]);
  const [departmentFilter, setDepartmentFilter] = useState([]);
  const [currentAction, setCurrentAction] = useState(null);
  const [leaveData, setLeaveData] = useState([]);
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();

  const navigate = useNavigate();


  const fetchActiveLeaves = async () => {
    setLoading(true);
    try {
      const response = await LeaveService.getAllLeave();
      if (response?.status) {
        const dataWithKeys = response.data.map((item) => ({
          key: item.id?.toString(),
          userID: item.uid,

          name: item.raised_by_user_name || 'N/A',
          department: item.department_name || 'N/A',
          fromDate: item.from_date,
          toDate: item.to_date,
          manager: item.reporting_manager_user_id || 'N/A',
          raisedOn: item.added_on,
          comment: item.note || '',
          status: item.status,
        }));
        const actLeaves = dataWithKeys.filter(item => item.status?.toLowerCase() === 'active');
        setLeaveData(actLeaves);
      } else {
        setLeaveData([]);
      }
    } catch (error) {
      console.error(error);
      setLeaveData([]);
    } finally {
      setLoading(false);
    }
  };


  const fetchManagers = async () => {
    try {
      const res = await UserService.getAllUsers();

      const filtered = res.data
        .filter(user => user.role?.toLowerCase() === 'manager' && user.status?.toLowerCase() === 'active')
        .map(user => {
          const uniqueId = user.uid || user.user_id || user.email || null;
          return { ...user, uid: uniqueId };
        })
        .filter(user => user.uid !== null);
      setManagers(filtered);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchActiveLeaves();
    fetchManagers();
  }, []);

  const splitDateTime = (dateTimeStr) => {
    if (!dateTimeStr) return { date: '', time: '' };
    const dt = moment(dateTimeStr);
    return { date: dt.format('YYYY-MM-DD'), time: dt.format('hh:mm A') };
  };

  const handleAction = (action, record) => {
    setSelectedRecord(record);
    setCurrentAction(action);
    setComment('');
    setIsModalOpen(true);
  };

  const handleModalCancel = () => {
    setIsModalOpen(false);
    setCurrentAction(null);
    setSelectedRecord(null);
    setComment('');
  };

  const handleModalAction = async () => {
    if (!selectedRecord || !currentAction) return;

    try {
      setLoading(true);

      const payload = {
        id: selectedRecord.key,
        status: currentAction,
        comment: comment,
      };


      const response = await LeaveService.cancelLeave(selectedRecord.key, payload);

      if (response?.status) {
        messageApi.success(`Leave request has been ${currentAction.toLowerCase()} successfully!`);
        fetchActiveLeaves();
        handleModalCancel();
      } else {
        messageApi.error('Failed to update leave request. Please try again.');
      }
    } catch (error) {
      console.error(error);
      messageApi.error('Error updating leave request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Sort leaveData by latest raisedOn first
  const sortedLeaveData = [...leaveData].sort(
    (a, b) => new Date(b.raisedOn) - new Date(a.raisedOn)
  );

  const filteredData = sortedLeaveData.filter((record) => {
    const searchLower = searchText.toLowerCase();
    const managerObj = managers.find(m => m.uid === record.manager);
    const managerName = managerObj ? managerObj.name.toLowerCase() : '';
    const matchesSearch =
      record.userID.toLowerCase().includes(searchLower) ||
      record.department.toLowerCase().includes(searchLower) ||
      managerName.includes(searchLower) ||
      (record.fromDate && record.fromDate.toLowerCase().includes(searchLower)) ||
      (record.toDate && record.toDate.toLowerCase().includes(searchLower)) ||
      (record.raisedOn && record.raisedOn.toLowerCase().includes(searchLower));
    const matchesDepartment =
      departmentFilter.length === 0 || departmentFilter.includes(record.department);
    let matchesDateRange = true;
    if (dateRange.length === 2) {
      const [start, end] = dateRange;
      const fromDate = moment(record.fromDate, 'YYYY-MM-DD');
      const toDate = moment(record.toDate, 'YYYY-MM-DD');
      matchesDateRange =
        toDate.isSameOrAfter(start, 'day') && fromDate.isSameOrBefore(end, 'day');
    }
    return matchesSearch && matchesDepartment && matchesDateRange;
  });


  const columns = [
    { title: 'S.No', key: 'serial', render: (_, __, i) => i + 1, width: 60, align: 'center' },
    { title: 'User ID', dataIndex: 'userID', key: 'userID', align: 'center' },
    { title: 'Name', dataIndex: 'name', key: 'name', align: 'center' },
    { title: 'Name', dataIndex: 'name', key: 'name', align: 'center' },

    { title: 'Department', dataIndex: 'department', key: 'department', align: 'center' },
    { title: 'From Date', dataIndex: 'fromDate', key: 'fromDate', align: 'center', render: (text) => text || '-' },
    { title: 'To Date', dataIndex: 'toDate', key: 'toDate', align: 'center', render: (text) => text || '-' },
    {
      title: 'Reporting Manager',
      dataIndex: 'manager',
      key: 'manager',
      align: 'center',
      render: (managerId) => {
        const manager = managers.find((m) => m.uid === managerId);
        return manager ? manager.name : managerId;
      },
    },
    {
      title: 'Raised On',
      key: 'raisedOn',
      align: 'center',
      render: (_, record) => {
        const { date, time } = splitDateTime(record.raisedOn);
        return `${date} ${time}`;
      },
    },
    {
      title: 'Action',
      key: 'action',
      align: 'center',
      render: (_, record) => (
        <Tooltip title={!perm.edit ? "You don't have permission to edit" : ""}>
          <Space wrap>
            <Button
              size="small"
              icon={<CheckOutlined />}
              onClick={() => handleAction('Approve', record)}
              disabled={!perm.edit}
              style={{ border: '1px solid #52c41a', color: '#52c41a', fontWeight: '500', padding: '18px' }}
            >
              Approve
            </Button>
            <Button
              size="small"
              icon={<CloseOutlined />}
              onClick={() => handleAction('Reject', record)}
              disabled={!perm.edit}
              style={{ border: '1px solid #ff4d4f', color: '#ff4d4f', fontWeight: '500', padding: '18px' }}
            >
              Reject
            </Button>
            <Button
              size="small"
              icon={<PauseOutlined />}
              onClick={() => handleAction('Hold', record)}
              disabled={!perm.edit}
              style={{ border: '1px solid #faad14', color: '#faad14', fontWeight: '500', padding: '18px' }}
            >
              Hold
            </Button>
          </Space>
        </Tooltip>
      ),
    },
  ];

  const tabsItems = [
    {
      key: 'leave',
      label: (<span><ProfileOutlined /> Active Leave Requests</span>),
      children: (
        <>
          <Card style={{ marginBottom: 20 }} >
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 20, alignItems: 'start' }}>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <label style={{ marginBottom: 6, fontWeight: 600 }}>Search</label>
                <Input placeholder="Search" value={searchText} onChange={(e) => setSearchText(e.target.value)} allowClear size="large" style={{ width: "70%" }} />
              </div>
              {/* <div style={{ display: 'flex', flexDirection: 'column' }}>
                <label style={{ marginBottom: 6, fontWeight: 600 }}>Select Date</label>
                <RangePicker
                  format="YYYY-MM-DD"
                  value={dateRange}
                  onChange={(dates) => setDateRange(dates || [])}
                  allowEmpty={[true, true]}
                  placeholder={['From Date', 'To Date']}
                  size="large"
                  style={{ width: '100%' }}
                />
              </div> */}
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <label style={{ marginBottom: 6, fontWeight: 600 }}>Select Department</label>
                <Select
                  mode="multiple"
                  placeholder="Select Department(s)"
                  allowClear
                  value={departmentFilter}
                  onChange={setDepartmentFilter}
                  size="large"
                  style={{ width: '70%' }}
                >
                  {Array.from(new Set(leaveData.map(item => item.department))).map(dept => (
                    <Option key={dept} value={dept}>
                      {dept}
                    </Option>
                  ))}
                </Select>
              </div>

            </div>
          </Card>
          <Table columns={columns} dataSource={filteredData} pagination={false} scroll={{ x: 'max-content' }} loading={loading} />
        </>
      ),
    },
    { key: 'manageLeave', label: (<span><SettingOutlined /> Leave Overview</span>), children: <ManageLeave /> },
    { key: 'addLeave', label: (<span><PlusOutlined /> Create Leave Type</span>), children: <AddLeave /> },
    { key: 'salaryslip', label: (<span><FileTextOutlined /> Employee Setup</span>), children: <SalarySlip /> },
    { key: 'breaktype', label: (<span><FieldTimeOutlined />{' '}Break Type</span>), children: <BreakType /> },
    {
      key: 'complaintRaise',
      label: (<span><FileTextOutlined /> Complaint Raise</span>),
      children: <ComplaintRaiseList />,
    },

  ];

  return (
    <div>
      {contextHolder}
      <Card
        title={
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: 18, fontWeight: 500 }}>Workforce Management</span>
            <Tooltip title={!perm.add ? "You don't have permission to create" : ""}>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => navigate("/manage-user")}
                disabled={!perm.add}
                style={{
                  backgroundColor: "#1677ff",
                  borderColor: "#1677ff",
                  fontWeight: 500,
                }}
              >
                Add Employee
              </Button>
            </Tooltip>
          </div>
        }
      >
        <Tabs
          type="card"
          activeKey={activeSection}
          onChange={setActiveSection}
          tabBarStyle={{ background: "#fff" }}
          items={tabsItems}
        />
      </Card>

      <Modal
        title={`${currentAction} Leave Request`}
        open={isModalOpen}
        onCancel={handleModalCancel}
        footer={[
          <Button
            key="action"
            type="default"
            danger={currentAction === 'Reject'}
            style={{
              borderColor: currentAction === 'Approve' ? '#52c41a' : currentAction === 'Hold' ? '#faad14' : undefined,
              color: currentAction === 'Approve' ? '#52c41a' : currentAction === 'Hold' ? '#faad14' : undefined,
              backgroundColor: 'transparent',
              boxShadow: 'none',
            }}
            onClick={handleModalAction}
            loading={loading}
          >
            {currentAction}
          </Button>,
        ]}
        centered
      >
        <TextArea rows={4} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Enter your comment here..." autoFocus />
      </Modal>
    </div>
  );
};

export default DisplayLeave;
